import { Inject, Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ApplianceCategory,
  Brand,
  BrandAppliance,
  CatalogueFacade,
} from '@domgen/dgx-fe-catalogue';
import { BrandCategoryAppliance } from '@domgen/dgx-fe-catalogue/lib/models/brand-category-appliance.interface';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import {
  ClientBrandCode,
  QueryParametersService,
  determineIsDesktop,
  filterNullUndefined,
} from '@domgen/dgx-fe-common';
import {
  controlValue,
  enableFormControls,
  pluckControl,
} from '@domgen/dgx-fe-forms/shared';
import { SteppedFormComponent } from '@domgen/dgx-fe-forms/stepped';
import { GAEvent, GAService } from '@domgen/dgx-fe-ga';
import { TranslateService } from '@domgen/dgx-fe-i18n';
import { ComponentStore } from '@ngrx/component-store';
import {
  AdobeCampaignService,
  PossibleNowService,
  RegistrationFacade,
  getActiveFeatureFlagVariant,
  getAdobeCampaignRequest,
  getPossibleNowRequest,
} from '@us-registration/data-access';
import {
  AdobeRequest,
  ApplianceDetailsSteppedForm,
  ApplianceDetailsSteppedFormConfig,
  ApplianceDetailsSteppedFormField,
  ApplianceDetailsSteppedFormValue,
  CmsApplianceDetailsSteppedPage,
  FindApplianceModelNumber,
  ModelAndSerialNumberFormField,
  PossibleNowRequest,
  REGISTRATION_CONFIG,
  RegistrationCodeForm,
  RegistrationCodeFormValue,
  RegistrationConfig,
  RegistrationRequest,
  RegistrationScreenEnabledVariant,
  RegistrationSystemCode,
  SerializationCodeResponse,
} from '@us-registration/util-models';
import { startOfToday, subMonths } from 'date-fns';
import { Observable, Subscription, combineLatest, interval, of } from 'rxjs';
import {
  filter,
  first,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { modelNumberLocation } from '../../constants/model-number-location';
import { RegistrationQueryParams } from '../../constants/registration-query-params.enum';
import { FeatureApplianceDetailsSteppedTranslationDictionary } from '../../models/i18n/feature-appliance-details-stepped-dictionary.interface';
import {
  determineRegistrationSystemCode,
  formatApplianceDetailsSteppedFormValueToRegistrationRequest,
} from '../../utils/appliance-details-stepped-form/appliance-details-stepped-form.utils';
import { ApplianceDetailsSteppedFormBuilder } from '../appliance-details-stepped-form-builder/appliance-details-stepped-form-builder.service';
import { SerializationService } from '../serialization/serialization.service';

export interface ViewModel {
  applianceCategoryCode?: string;
  modelNumberFinderContent: FindApplianceModelNumber | undefined;
  applianceCode?: string;
  applianceMaximumAge: Date;
  brandAppliance?: BrandAppliance | BrandCategoryAppliance;
  brandApplianceCategories?: ApplianceCategory[];
  brandApplianceCategoriesLoading: boolean;
  brand?: Brand;
  brandCategoryAppliancesLoading: boolean;
  brandCode?: string;
  clientBrandCode?: ClientBrandCode;
  cmsData: CmsApplianceDetailsSteppedPage;
  displayFetchingQuoteLoadingOverlay: boolean;
  displayRegistrationCodeConfirmationPanel: boolean;
  fetchingQuoteProgressValue: number;
  form?: FormGroup<ApplianceDetailsSteppedForm>;
  hasIncorrectModelAndSerialNumber: boolean;
  hasRegistrationCode: boolean;
  isCurrencyStep: boolean;
  isDesktop: boolean;
  isPhotoRegJourney: boolean;
  loadingSubmitRegistrationCode: boolean;
  modelNumberSearchEnabled: boolean;
  modelSerialNumberFieldsetDescription?: string;
  navigatedToPriceStep: boolean;
  registrationCode?: string;
  registrationCodeForm?: FormGroup<RegistrationCodeForm>;
  registrationLoading: boolean;
  registrationRequest?: RegistrationRequest;
  selectedBrandApplianceCategory?: string;
  selectedBrandApplianceName?: string;
  serializationCodeResponse?: SerializationCodeResponse;
  showRegistrationScreen: boolean;
}

export interface ComponentState {
  adobeRequest?: AdobeRequest;
  applianceMaximumAge: Date;
  modelNumberFinderContent: FindApplianceModelNumber | undefined;
  brand?: Brand;
  brandAppliance?: BrandAppliance | BrandCategoryAppliance;
  brandApplianceCategories?: ApplianceCategory[];
  brandCategoryAppliances?: BrandCategoryAppliance[];
  clientBrandCode?: ClientBrandCode;
  displayFetchingQuoteLoadingOverlay: boolean;
  displayRegistrationCodeConfirmationPanel: boolean;
  fetchingQuoteProgressValue: number;
  form?: FormGroup<ApplianceDetailsSteppedForm>;
  hasIncorrectModelAndSerialNumber: boolean;
  hasRegistrationCode: boolean;
  hasSubmittedRegistrationCodeForm: boolean;
  isCurrencyStep: boolean;
  isPhotoRegJourney: boolean;
  loadingSubmitRegistrationCode: boolean;
  modelNumberSearchEnabled: boolean;
  modelSerialNumberFieldsetDescription?: string;
  navigatedToPriceStep: boolean;
  partsWarranty?: number;
  possibleNowRequest?: PossibleNowRequest;
  registrationCode?: string;
  registrationCodeForm?: FormGroup<RegistrationCodeForm>;
  registrationRequest?: RegistrationRequest;
  selectedBrandApplianceCategory?: string;
  selectedBrandApplianceName?: string;
  serializationCodeResponse?: SerializationCodeResponse;
  steppedFormComponent?: SteppedFormComponent;
  systemCode: RegistrationSystemCode;
  showRegistrationScreen: boolean;
}

@Injectable()
export class ApplianceDetailsSteppedFormComponentService extends ComponentStore<ComponentState> {
  private readonly adobeRequest$: Observable<AdobeRequest | undefined> =
    this.select((state: ComponentState) => state.adobeRequest);

  private readonly possibleNowRequest$: Observable<
    PossibleNowRequest | undefined
  > = this.select((state: ComponentState) => state.possibleNowRequest);

  private readonly applianceMaximumAge$: Observable<Date> = this.select(
    (state: ComponentState) => state.applianceMaximumAge
  );

  private readonly brand$: Observable<Brand | undefined> = this.select(
    (state) => state.brand
  );

  private readonly brandAppliance$: Observable<BrandAppliance | undefined> =
    this.select((state) => state.brandAppliance);

  private readonly brandApplianceCategories$: Observable<
    ApplianceCategory[] | undefined
  > = this.select((state) => state.brandApplianceCategories);

  private readonly brandCategoryAppliances$: Observable<
    BrandCategoryAppliance[] | undefined
  > = this.select((state) => state.brandCategoryAppliances);

  private readonly clientBrandCode$: Observable<ClientBrandCode | undefined> =
    this.select((state: ComponentState) => state.clientBrandCode);

  private readonly displayRegistrationCodeConfirmationPanel$: Observable<boolean> =
    this.select(
      (state: ComponentState) => state.displayRegistrationCodeConfirmationPanel
    );

  private readonly displayFetchingQuoteLoadingOverlay$: Observable<boolean> =
    this.select(
      (state: ComponentState) => state.displayFetchingQuoteLoadingOverlay
    );

  private readonly fetchingQuoteProgressValue$: Observable<number> =
    this.select((state: ComponentState) => state.fetchingQuoteProgressValue);

  private readonly form$: Observable<
    FormGroup<ApplianceDetailsSteppedForm> | undefined
  > = this.select((state: ComponentState) => state.form);

  private readonly isPhotoRegJourney$: Observable<boolean> = this.select(
    (state: ComponentState) => state.isPhotoRegJourney
  );

  private readonly isCurrencyStep$: Observable<boolean> = this.select(
    (state: ComponentState) => state.isCurrencyStep
  );

  private readonly registrationCodeForm$: Observable<
    FormGroup<RegistrationCodeForm> | undefined
  > = this.select((state: ComponentState) => state.registrationCodeForm);

  private readonly hasIncorrectModelAndSerialNumber$: Observable<boolean> =
    this.select(
      (state: ComponentState) => state.hasIncorrectModelAndSerialNumber
    );

  private readonly hasRegistrationCode$: Observable<boolean> = this.select(
    (state: ComponentState) => state.hasRegistrationCode
  );

  private readonly hasSubmittedRegistrationCodeForm$: Observable<boolean> =
    this.select(
      (state: ComponentState) => state.hasSubmittedRegistrationCodeForm
    );

  private readonly navigatedToPriceStep$: Observable<boolean> = this.select(
    (state: ComponentState) => state.navigatedToPriceStep
  );

  private readonly applianceCode$: Observable<string | undefined> =
    this.form$.pipe(
      controlValue<string>(ApplianceDetailsSteppedFormField.ApplianceCode)
    );

  private readonly applianceCategoryCode$: Observable<string | undefined> =
    this.form$.pipe(
      controlValue<string>(
        ApplianceDetailsSteppedFormField.ApplianceCategoryCode
      )
    );

  private readonly modelNumberFinderContent$: Observable<
    FindApplianceModelNumber | undefined
  > = this.select((state: ComponentState) => state.modelNumberFinderContent);

  private readonly modelSerialNumberFieldsetDescription$: Observable<
    string | undefined
  > = this.select(
    (state: ComponentState) => state.modelSerialNumberFieldsetDescription
  );

  private readonly brandCode$: Observable<string | undefined> = this.form$.pipe(
    controlValue<string>(ApplianceDetailsSteppedFormField.BrandCode)
  );

  private readonly initialValue$: Observable<
    Partial<ApplianceDetailsSteppedFormValue>
  > = combineLatest({
    modelNumber: this.queryParametersService.get(
      RegistrationQueryParams.ModelNumber
    ),
    serialNumber: this.queryParametersService.get(
      RegistrationQueryParams.SerialNumber
    ),
    registrationSource: this.queryParametersService.get(
      RegistrationQueryParams.RegistrationSource
    ),
  }).pipe(
    take(1),
    mergeMap(({ modelNumber, serialNumber, registrationSource }) => {
      const systemCode: RegistrationSystemCode =
        determineRegistrationSystemCode(modelNumber, registrationSource);

      this.updateSystemCode(systemCode);
      localStorage.setItem('systemCode', systemCode);
      localStorage.setItem('registrationCodeUsed', 'false');
      this.registrationFacade.addRegistrationSystemCode(systemCode);

      this.gaService.pageView({
        event: GAEvent.PageView,
        pagename: 'appliance-details-form',
        regSystemCode: systemCode,
      });

      const defaultValue: Partial<ApplianceDetailsSteppedFormValue> = {
        applianceCategoryCode: undefined,
        applianceCode: undefined,
        brandCode: this.registrationConfig.clientBrandCode,
        modelAndSerialNumber: {
          modelNumber: null,
          serialNumber: null,
        },
      };

      if (!modelNumber || !serialNumber) {
        return of(defaultValue);
      }

      this.updateDisplayRegistrationCodeConfirmationPanel(true);

      return this.serializationService
        .submitModelAndSerialNumber(modelNumber, serialNumber)
        .pipe(
          map((response) => {
            if (!response) {
              return defaultValue;
            }
            this.updateSerializationCodeResponse(response);

            return {
              applianceCategoryCode: response.categoryCode,
              applianceCode: response.applianceCode,
              brandCode: this.registrationConfig.clientBrandCode,
              modelAndSerialNumber: {
                modelNumber: response.Model_Number,
                serialNumber: response.Serial_Number,
              },
              selectedBrandApplianceName: response.Type,
            };
          })
        );
    })
  );

  private readonly formConfig$: Observable<ApplianceDetailsSteppedFormConfig> =
    combineLatest({
      applianceMaximumAge: this.applianceMaximumAge$,
      clientBrandCode: this.clientBrandCode$,
      cmsData: this.cmsContainer.content$,
      initialValue: this.initialValue$,
    });

  readonly showRegistrationScreen$: Observable<boolean> = this.select(
    (state) => state.showRegistrationScreen
  );

  private readonly loadingSubmitRegistrationCode$: Observable<boolean> =
    this.select((state: ComponentState) => state.loadingSubmitRegistrationCode);

  private readonly modelNumberSearchEnabled$: Observable<boolean> = this.select(
    (state) => state.modelNumberSearchEnabled
  );

  private readonly selectedBrandApplianceCategory$: Observable<
    string | undefined
  > = this.select(
    (state: ComponentState) => state.selectedBrandApplianceCategory
  );

  private readonly selectedBrandApplianceName$: Observable<string | undefined> =
    this.select((state: ComponentState) => state.selectedBrandApplianceName);

  private readonly serializationCodeResponse$: Observable<
    SerializationCodeResponse | undefined
  > = this.select((state: ComponentState) => state.serializationCodeResponse);

  private readonly systemCode$: Observable<RegistrationSystemCode> =
    this.select((state) => state.systemCode);

  private readonly modelAndSerialNumberFieldset$: Observable<AbstractControl> =
    this.form$.pipe(
      pluckControl(ApplianceDetailsSteppedFormField.ModelAndSerialNumber)
    );

  readonly vm$: Observable<ViewModel> = combineLatest({
    adobeRequest: this.adobeRequest$,
    applianceCode: this.applianceCode$,
    applianceCategoryCode: this.applianceCategoryCode$,
    applianceMaximumAge: this.applianceMaximumAge$,
    modelNumberFinderContent: this.modelNumberFinderContent$,
    brandApplianceCategories: this.brandApplianceCategories$,
    brandAppliance: this.brandAppliance$,
    brand: this.brand$,
    brandCategoryAppliancesLoading:
      this.catalogueFacade.brandCategoryAppliancesLoading$,
    brandApplianceCategoriesLoading:
      this.catalogueFacade.brandApplianceCategoriesLoading$,
    brandCode: this.brandCode$,
    clientBrandCode: this.clientBrandCode$,
    cmsData: this.cmsContainer.content$,
    displayFetchingQuoteLoadingOverlay:
      this.displayFetchingQuoteLoadingOverlay$,
    fetchingQuoteProgressValue: this.fetchingQuoteProgressValue$,
    displayRegistrationCodeConfirmationPanel:
      this.displayRegistrationCodeConfirmationPanel$,
    form: this.form$,
    isCurrencyStep: this.isCurrencyStep$,
    isPhotoRegJourney: this.isPhotoRegJourney$,
    registrationCodeForm: this.registrationCodeForm$,
    hasRegistrationCode: this.hasRegistrationCode$,
    hasIncorrectModelAndSerialNumber: this.hasIncorrectModelAndSerialNumber$,
    isDesktop: determineIsDesktop(),
    loadingSubmitRegistrationCode: this.loadingSubmitRegistrationCode$,
    modelNumberSearchEnabled: this.modelNumberSearchEnabled$,
    modelSerialNumberFieldsetDescription:
      this.modelSerialNumberFieldsetDescription$,
    navigatedToPriceStep: this.navigatedToPriceStep$,
    registrationLoading: this.registrationFacade.registrationLoading$,
    registrationRequest: this.registrationFacade.registrationRequest$,
    serializationCodeResponse: this.serializationCodeResponse$,
    selectedBrandApplianceCategory: this.selectedBrandApplianceCategory$,
    selectedBrandApplianceName: this.selectedBrandApplianceName$,
    showRegistrationScreen: this.showRegistrationScreen$,
  });

  constructor(
    @Inject(REGISTRATION_CONFIG)
    private readonly registrationConfig: RegistrationConfig,
    private readonly applianceDetailsSteppedFormBuilder: ApplianceDetailsSteppedFormBuilder,
    private readonly catalogueFacade: CatalogueFacade,
    private readonly cmsContainer: CmsContainer<CmsApplianceDetailsSteppedPage>,
    private readonly queryParametersService: QueryParametersService,
    private readonly registrationFacade: RegistrationFacade,
    private readonly route: ActivatedRoute,
    private readonly serializationService: SerializationService,
    private readonly adobeCampaignService: AdobeCampaignService,
    private readonly possibleNowService: PossibleNowService,
    private readonly gaService: GAService,
    private readonly translateService: TranslateService<FeatureApplianceDetailsSteppedTranslationDictionary>
  ) {
    super({
      modelNumberFinderContent: undefined,
      applianceMaximumAge: subMonths(
        startOfToday(),
        registrationConfig.applianceMaximumAgeInMonths
      ),
      displayFetchingQuoteLoadingOverlay: false,
      fetchingQuoteProgressValue: 0,
      displayRegistrationCodeConfirmationPanel: false,
      hasIncorrectModelAndSerialNumber: false,
      hasRegistrationCode: true,
      hasSubmittedRegistrationCodeForm: false,
      isPhotoRegJourney: false,
      isCurrencyStep: false,
      loadingSubmitRegistrationCode: false,
      modelNumberSearchEnabled: false,
      modelSerialNumberFieldsetDescription: undefined,
      navigatedToPriceStep: false,
      systemCode: RegistrationSystemCode.InternetProductRegistration,
      showRegistrationScreen: true,
    });
  }

  readonly updateShowRegistrationScreen = this.updater(
    (state: ComponentState, showRegistrationScreen: boolean) => ({
      ...state,
      showRegistrationScreen,
    })
  )(
    of(
      getActiveFeatureFlagVariant(
        'registration-screen-enabled',
        RegistrationScreenEnabledVariant
      ) === RegistrationScreenEnabledVariant.One
    )
  );

  readonly updateAdobeRequest = this.updater(
    (state: ComponentState, adobeRequest: AdobeRequest | undefined) => ({
      ...state,
      adobeRequest,
    })
  )(
    combineLatest({
      adobeCampaignBrand: of(this.registrationConfig.adobeCampaignBrand),
      adobeCampaignEventType: of(
        this.registrationConfig.adobeCampaignEventType
      ),
      registrationRequest: this.registrationFacade.registrationRequest$,
    }).pipe(
      map(
        ({ adobeCampaignBrand, adobeCampaignEventType, registrationRequest }) =>
          getAdobeCampaignRequest(
            adobeCampaignBrand as string,
            adobeCampaignEventType as string,
            registrationRequest
          )
      )
    )
  );

  readonly updatePossibleNowRequest = this.updater(
    (
      state: ComponentState,
      possibleNowRequest: PossibleNowRequest | undefined
    ) => ({
      ...state,
      possibleNowRequest,
    })
  )(
    combineLatest({
      possibleNowFilterId: of(this.registrationConfig.possibleNowFilterId),
      registrationSystemCode: this.registrationFacade.registrationSystemCode$,
      registrationRequest: this.registrationFacade.registrationRequest$,
    }).pipe(
      map(
        ({
          possibleNowFilterId,
          registrationSystemCode,
          registrationRequest,
        }) =>
          getPossibleNowRequest(
            possibleNowFilterId as string,
            registrationSystemCode,
            registrationRequest
          )
      )
    )
  );

  readonly updateModelSerialFieldsetDescription = this.updater(
    (
      state: ComponentState,
      modelSerialNumberFieldsetDescription: string | undefined
    ) => ({
      ...state,
      modelSerialNumberFieldsetDescription,
    })
  )(
    combineLatest([
      this.serializationCodeResponse$,
      this.hasSubmittedRegistrationCodeForm$,
      this.cmsContainer.content$,
    ]).pipe(
      map(
        ([
          serializationCodeResponse,
          hasSubmittedRegistrationCodeForm,
          cmsData,
        ]) => {
          const defaultModelSerialNumberFieldsetDescription =
            cmsData.modelAndSerialNumberFieldset.fieldsetLegendNote;

          if (!hasSubmittedRegistrationCodeForm) {
            return defaultModelSerialNumberFieldsetDescription;
          }

          if (!serializationCodeResponse) {
            return this.translateService.translate(
              'APPLIANCE_DETAILS_STEPPED_FORM_MODEL_SERIAL_FIELDSET_INVALID_SERIALISATION_CODE_DESCRIPTION'
            );
          }

          return this.translateService.translate(
            'APPLIANCE_DETAILS_STEPPED_FORM_FIELDSET_LEGEND_NOTE_SERIALIZATION_RESPONSE_SUCCESS',
            {
              registrationCode: serializationCodeResponse.Serialization_Code,
            }
          );
        }
      )
    )
  );

  readonly updateLoadingSubmitRegistrationCode = this.updater(
    (state: ComponentState, loadingSubmitRegistrationCode: boolean) => ({
      ...state,
      loadingSubmitRegistrationCode,
    })
  );

  private readonly updateBrandApplianceCategories = this.updater(
    (
      state: ComponentState,
      brandApplianceCategories?: ApplianceCategory[]
    ) => ({
      ...state,
      brandApplianceCategories,
    })
  )(
    this.brandCode$.pipe(
      switchMap((brandCode: string | undefined) =>
        brandCode
          ? this.catalogueFacade.brandApplianceCategories(brandCode)
          : of([])
      )
    )
  );

  private readonly updateBrandCategoryAppliances = this.updater(
    (
      state: ComponentState,
      brandCategoryAppliances?: BrandCategoryAppliance[]
    ) => ({
      ...state,
      brandCategoryAppliances,
    })
  )(
    combineLatest([this.brandCode$, this.applianceCategoryCode$]).pipe(
      switchMap(([brandCode, applianceCategoryCode]) =>
        brandCode && applianceCategoryCode
          ? this.catalogueFacade.brandCategoryAppliancesByCode(
              brandCode,
              applianceCategoryCode
            )
          : of(undefined)
      )
    )
  );

  private readonly updateSelectedBrandApplianceCategory = this.updater(
    (state: ComponentState, selectedBrandApplianceCategory?: string) => ({
      ...state,
      selectedBrandApplianceCategory,
    })
  )(
    combineLatest([
      this.applianceCategoryCode$,
      this.brandApplianceCategories$,
    ]).pipe(
      map(([applianceCategoryCode, brandApplianceCategories]) => {
        const selectedOption: ApplianceCategory | undefined =
          brandApplianceCategories?.find(
            (brandApplianceCategory: ApplianceCategory) =>
              brandApplianceCategory.categoryCode === applianceCategoryCode
          );
        return selectedOption?.categoryName;
      })
    )
  );

  private readonly updateSelectedBrandApplianceName = this.updater(
    (state: ComponentState, selectedBrandApplianceName?: string) => ({
      ...state,
      selectedBrandApplianceName,
    })
  )(
    combineLatest([this.applianceCode$, this.brandCategoryAppliances$]).pipe(
      map(([applianceCode, brandCategoryAppliances]) => {
        const selectedOption: BrandCategoryAppliance | undefined =
          brandCategoryAppliances?.find(
            (brandCategoryAppliance: BrandCategoryAppliance) =>
              brandCategoryAppliance.applianceCode === applianceCode
          );

        return selectedOption?.applianceName;
      })
    )
  );

  private readonly updateIsPhotoRegJourney = this.updater(
    (state: ComponentState, isPhotoRegJourney: boolean) => ({
      ...state,
      isPhotoRegJourney,
    })
  )(
    this.registrationFacade.registrationSystemCode$.pipe(
      map(
        (registrationSystemCode) =>
          registrationSystemCode ===
          RegistrationSystemCode.PhotoregProductRegistration
      )
    )
  );

  private readonly updateIsCurrencyStep = this.updater(
    (state: ComponentState, isCurrencyStep: boolean) => ({
      ...state,
      isCurrencyStep,
    })
  );

  private readonly updateBrand = this.updater(
    (state: ComponentState, brand?: Brand) => ({
      ...state,
      brand,
    })
  )(
    this.brandCode$.pipe(
      switchMap((brandCode: string | undefined) =>
        brandCode ? this.catalogueFacade.brandByCode(brandCode) : of(undefined)
      )
    )
  );

  private readonly updateBrandAppliance = this.updater(
    (
      state: ComponentState,
      brandAppliance?: BrandAppliance | BrandCategoryAppliance
    ) => ({
      ...state,
      brandAppliance,
    })
  )(
    combineLatest([
      this.brandCode$,
      this.applianceCode$,
      this.applianceCategoryCode$,
    ]).pipe(
      switchMap(
        ([brandCode, applianceCode, categoryCode]: (string | undefined)[]) => {
          if (brandCode && applianceCode) {
            if (categoryCode) {
              return this.catalogueFacade.brandCategoryApplianceByCode(
                brandCode,
                categoryCode,
                applianceCode
              );
            }
            return this.catalogueFacade.brandApplianceByCode(
              brandCode,
              applianceCode
            );
          }

          return of(undefined);
        }
      )
    )
  );

  readonly updateClientBrandCode = this.updater(
    (state: ComponentState, clientBrandCode?: ClientBrandCode) => ({
      ...state,
      clientBrandCode,
    })
  );

  private readonly updateForm = this.updater(
    (state: ComponentState, form: FormGroup<ApplianceDetailsSteppedForm>) => ({
      ...state,
      form,
    })
  );

  readonly updateNavigatedToPriceStep = this.updater(
    (state: ComponentState, navigatedToPriceStep: boolean) => ({
      ...state,
      navigatedToPriceStep,
    })
  );

  readonly updateDisplayRegistrationCodeConfirmationPanel = this.updater(
    (
      state: ComponentState,
      displayRegistrationCodeConfirmationPanel: boolean
    ) => ({
      ...state,
      displayRegistrationCodeConfirmationPanel,
    })
  );

  private readonly updateRegistrationCodeForm = this.updater(
    (
      state: ComponentState,
      registrationCodeForm: FormGroup<RegistrationCodeForm>
    ) => ({
      ...state,
      registrationCodeForm,
    })
  );

  readonly updateSerializationCodeResponse = this.updater(
    (
      state: ComponentState,
      serializationCodeResponse?: SerializationCodeResponse
    ) => ({
      ...state,
      serializationCodeResponse,
    })
  );

  readonly updateSystemCode = this.updater(
    (state: ComponentState, systemCode: RegistrationSystemCode) => ({
      ...state,
      systemCode,
    })
  );

  readonly updateModelNumberFinderContent = this.updater(
    (
      state: ComponentState,
      modelNumberFinderContent: FindApplianceModelNumber | undefined
    ) => ({
      ...state,
      modelNumberFinderContent,
    })
  )(
    combineLatest([this.brandCode$, this.brandAppliance$]).pipe(
      map(([brandCode, brandAppliance]): FindApplianceModelNumber | undefined =>
        brandCode && brandAppliance
          ? modelNumberLocation[brandCode].find(
              (appliance: FindApplianceModelNumber) =>
                appliance.product === brandAppliance.applianceName.toUpperCase()
            )
          : undefined
      )
    )
  );

  readonly updateModelNumberSearchEnabled = this.updater(
    (state: ComponentState, modelNumberSearchEnabled: boolean) => ({
      ...state,
      modelNumberSearchEnabled,
    })
  )(
    this.brandCode$.pipe(
      filter((brandCode): brandCode is string => brandCode !== undefined),
      map((brandCode) =>
        this.registrationConfig.modelNumberSearchEnabledBrands.includes(
          brandCode as ClientBrandCode
        )
      )
    )
  );

  private readonly updatePartsWarranty = this.updater(
    (state: ComponentState, partsWarranty?: number) => ({
      ...state,
      partsWarranty,
    })
  )(
    combineLatest([this.brandCode$, this.applianceCode$]).pipe(
      map(([brandCode, applianceCode]) => {
        const partsWarranty = this.registrationConfig.partsWarranty;

        if (!brandCode || !applianceCode || !partsWarranty) {
          return undefined;
        }

        const brandPartsWarranty = partsWarranty[brandCode];

        if (
          brandPartsWarranty.excludedApplianceCodes?.includes(applianceCode)
        ) {
          return undefined;
        }
        return brandPartsWarranty.partsWarrantyInMonths;
      })
    )
  );

  updateSteppedFormComponent = this.updater(
    (state: ComponentState, steppedFormComponent?: SteppedFormComponent) => ({
      ...state,
      steppedFormComponent,
    })
  );

  updateHasRegistrationCode = this.updater(
    (state: ComponentState, hasRegistrationCode: boolean) => ({
      ...state,
      hasRegistrationCode,
    })
  );

  updateSubmittedRegistrationCodeForm = this.updater(
    (state: ComponentState, hasSubmittedRegistrationCodeForm: boolean) => ({
      ...state,
      hasSubmittedRegistrationCodeForm,
    })
  );

  updateHasIncorrectModelAndSerialNumber = this.updater(
    (state: ComponentState, hasIncorrectModelAndSerialNumber: boolean) => ({
      ...state,
      hasIncorrectModelAndSerialNumber,
    })
  );

  readonly updateDisplayFetchingQuoteLoadingOverlay = this.updater(
    (state: ComponentState, displayFetchingQuoteLoadingOverlay: boolean) => ({
      ...state,
      displayFetchingQuoteLoadingOverlay: displayFetchingQuoteLoadingOverlay,
    })
  );

  readonly updateFetchingQuoteProgressValue = this.updater(
    (state: ComponentState, fetchingQuoteProgressValue: number) => ({
      ...state,
      fetchingQuoteProgressValue: fetchingQuoteProgressValue,
    })
  );

  readonly buildForm = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      mergeMap(() => this.formConfig$),
      first(),
      map((config: ApplianceDetailsSteppedFormConfig) =>
        this.applianceDetailsSteppedFormBuilder.buildApplianceDetailsSteppedForm(
          config
        )
      ),
      tap((form) => this.updateForm(form))
    )
  );

  readonly buildRegistrationCodeForm = this.effect(
    (trigger$: Observable<void>) =>
      trigger$.pipe(
        map(() =>
          this.applianceDetailsSteppedFormBuilder.buildRegistrationCodeForm()
        ),
        tap((form) => this.updateRegistrationCodeForm(form))
      )
  );

  readonly startFetchingQuoteProgress = this.effect(
    (trigger$: Observable<void>) =>
      trigger$.pipe(
        switchMap(() => interval(200).pipe(take(101))),
        tap((newValue) => this.updateFetchingQuoteProgressValue(newValue))
      )
  );

  private readonly enableModelAndSerialFieldsBasedOnHasRegistrationCode: Subscription =
    this.effect((trigger$: Observable<boolean>) =>
      combineLatest([
        trigger$,
        this.form$.pipe(
          pluckControl(ApplianceDetailsSteppedFormField.ModelAndSerialNumber)
        ),
      ]).pipe(
        tap(([hasRegistrationCode, modelAndSerialNumber]) => {
          const modelNumberField: AbstractControl | null =
            modelAndSerialNumber.get(ModelAndSerialNumberFormField.ModelNumber);
          const serialNumberField: AbstractControl | null =
            modelAndSerialNumber.get(
              ModelAndSerialNumberFormField.SerialNumber
            );

          if (!hasRegistrationCode) {
            enableFormControls(modelNumberField);
            enableFormControls(serialNumberField);
          }
        })
      )
    )(this.hasRegistrationCode$);

  private readonly setModelAndSerialNumberFieldsValue: Subscription =
    this.effect(
      (
        trigger$: Observable<
          [AbstractControl, SerializationCodeResponse | undefined]
        >
      ) =>
        trigger$.pipe(
          tap(([modelAndSerialNumberFieldset, serializationCodeResponse]) => {
            const modelNumberField = modelAndSerialNumberFieldset.get(
              ModelAndSerialNumberFormField.ModelNumber
            ) as AbstractControl;
            const serialNumberField = modelAndSerialNumberFieldset.get(
              ModelAndSerialNumberFormField.SerialNumber
            ) as AbstractControl;

            if (!serializationCodeResponse) {
              modelNumberField.patchValue(null);
              serialNumberField.patchValue(null);
              this.updateHasRegistrationCode(false);
            }

            if (serializationCodeResponse) {
              modelNumberField.patchValue(
                serializationCodeResponse.Model_Number
              );
              serialNumberField.patchValue(
                serializationCodeResponse.Serial_Number
              );
            }
          })
        )
    )(
      combineLatest([
        this.modelAndSerialNumberFieldset$,
        this.serializationCodeResponse$,
      ])
    );

  private readonly trackSerialisationSuccessGAEvents: Subscription =
    this.effect(
      (
        trigger$: Observable<
          [SerializationCodeResponse | undefined, string, string]
        >
      ) =>
        trigger$.pipe(
          tap(
            ([
              serializationCodeResponse,
              registrationSystemCode,
              systemCode,
            ]) => {
              if (serializationCodeResponse) {
                this.gaService.pageView({
                  event: GAEvent.PageView,
                  pagename: 'appliance-details-code-success',
                  regSystemCode: registrationSystemCode,
                  applianceName: serializationCodeResponse.Type,
                });

                this.gaService.tag({
                  pagename: 'appliance-details-code-success',
                  eventAction: 'modelNumber-auto',
                  eventLabel: serializationCodeResponse.Model_Number,
                  applianceName: serializationCodeResponse.Type,
                  eventCategory: 'appliance details form USA',
                  event: GAEvent.GenericEvent,
                  regSystemCode: systemCode,
                });

                this.gaService.tag({
                  pagename: 'appliance-details-code-success',
                  eventAction: 'serialNumber-auto',
                  eventLabel: serializationCodeResponse.Serial_Number,
                  eventCategory: 'appliance details form USA',
                  applianceName: serializationCodeResponse.Type,
                  event: GAEvent.GenericEvent,
                  regSystemCode: systemCode,
                });
              }
            }
          )
        )
    )(
      combineLatest([
        this.serializationCodeResponse$,
        this.registrationFacade.registrationSystemCode$,
        this.systemCode$,
      ])
    );

  readonly listenToRouteFragmentChanges = this.effect(() =>
    this.route.fragment.pipe(
      tap((fragment) => {
        this.updateIsCurrencyStep(false);

        if (fragment === '3') {
          this.updateHasRegistrationCode(true);
        }
        if (fragment === '4') {
          this.updateIsCurrencyStep(true);
        }
      })
    )
  );

  private readonly sendToAdobeCampaign: Subscription = this.effect(
    (trigger$: Observable<AdobeRequest | undefined>) =>
      trigger$.pipe(
        filterNullUndefined(),
        switchMap((adobeRequest) =>
          this.adobeCampaignService.sendToAdobeCampaign(adobeRequest)
        )
      )
  )(this.adobeRequest$);

  private readonly sendToPossibleNow: Subscription = this.effect(
    (trigger$: Observable<PossibleNowRequest | undefined>) =>
      trigger$.pipe(
        filterNullUndefined(),
        switchMap((possibleNowRequest) =>
          this.possibleNowService.sendToPossibleNow([possibleNowRequest])
        )
      )
  )(this.possibleNowRequest$);

  onRegistrationCodeSubmit(
    formValue: RegistrationCodeFormValue
  ): Observable<SerializationCodeResponse> {
    this.updateLoadingSubmitRegistrationCode(true);
    this.updateSubmittedRegistrationCodeForm(true);
    localStorage.setItem('registrationCodeUsed', 'true');

    return this.serializationService
      .submitRegistrationCode(formValue.registrationCode)
      .pipe(
        tap((response) => {
          this.updateLoadingSubmitRegistrationCode(false);
          this.updateSerializationCodeResponse(response);
          this.registrationFacade.addSerializationCodeResponse(response);
        })
      );
  }

  onFormSubmit(
    formValue: ApplianceDetailsSteppedFormValue
  ): Observable<RegistrationRequest> {
    return combineLatest({
      adobeRequest: this.adobeRequest$,
      brand: this.brand$.pipe(filterNullUndefined()),
      brandAppliance: this.brandAppliance$.pipe(filterNullUndefined()),
      systemCode: this.systemCode$,
      serializationCodeResponse: this.serializationCodeResponse$,
    }).pipe(
      first(),
      map((response) =>
        formatApplianceDetailsSteppedFormValueToRegistrationRequest(
          formValue,
          response.brandAppliance,
          response.brand,
          response.systemCode,
          response.serializationCodeResponse
        )
      ),
      filterNullUndefined()
    );
  }
}
